/**
 * Lazy loads videos in product sliders and teasers using IntersectionObserver
 * Videos with class 'product-video-lazy' or 'teaser-video-lazy' will only load their sources when entering viewport
 */

const INTERSECTION_THRESHOLD = 0.25;
const ROOT_MARGIN = '50px';

// Track observed videos to prevent duplicate observations
const observedVideos = new WeakSet();
let observer = null;

function shouldAutoPlay() {
    return !window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function initVideoLazyLoading() {
    // Create observer only once
    if (!observer) {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const video = entry.target;

                    if (entry.isIntersecting) {
                        if (!video.dataset.loaded) {
                            loadVideoSources(video);
                            video.dataset.loaded = 'true';
                        }

                        // Only autoplay when the user hasn't explicitly paused the video.
                        // (Scrolling out/in should not override user intent.)
                        const userPaused = video.dataset.userPaused === 'true';
                        if (!userPaused && shouldAutoPlay()) {
                            video.play().catch(err => {
                                console.warn('Video autoplay failed:', err);
                            });
                        }
                    } else if (!entry.isIntersecting && video.dataset.loaded) {
                        // Pause video when out of viewport, but don't mark as user-paused.
                        if (!video.paused) {
                            video.dataset.autoPaused = 'true';
                            video.pause();
                        }
                    }
                });
            },
            {
                threshold: INTERSECTION_THRESHOLD,
                rootMargin: ROOT_MARGIN
            }
        );
    }

    // Find all videos and observe only new ones
    const videos = document.querySelectorAll('.product-video-lazy, .teaser-video-lazy');

    videos.forEach((video) => {
        if (!observedVideos.has(video)) {
            observer.observe(video);
            observedVideos.add(video);
        }
    });
}

function loadVideoSources(video) {
    // Handle product slider videos (data-video-large, data-video-small)
    const videoLarge = video.dataset.videoLarge;
    const videoSmall = video.dataset.videoSmall;

    // Handle teaser videos (data-video-desktop, data-video-tablet, data-video-default)
    const videoDesktop = video.dataset.videoDesktop;
    const videoTablet = video.dataset.videoTablet;
    const videoDefault = video.dataset.videoDefault;
    const videoType = video.dataset.videoType || 'video/mp4';

    if (videoLarge && videoSmall) {
        // Product slider format
        const sourceLarge = document.createElement('source');
        sourceLarge.src = videoLarge;
        sourceLarge.type = videoType;
        sourceLarge.media = '(min-width: 992px)';

        const sourceSmall = document.createElement('source');
        sourceSmall.src = videoSmall;
        sourceSmall.type = videoType;

        video.appendChild(sourceLarge);
        video.appendChild(sourceSmall);
    } else if (videoDefault) {
        // Teaser format
        if (videoDesktop) {
            const sourceDesktop = document.createElement('source');
            sourceDesktop.src = videoDesktop;
            sourceDesktop.type = videoType;
            sourceDesktop.media = '(min-width: 1280px)';
            video.appendChild(sourceDesktop);
        }

        if (videoTablet) {
            const sourceTablet = document.createElement('source');
            sourceTablet.src = videoTablet;
            sourceTablet.type = videoType;
            sourceTablet.media = '(min-width: 768px)';
            video.appendChild(sourceTablet);
        }

        const sourceDefault = document.createElement('source');
        sourceDefault.src = videoDefault;
        sourceDefault.type = videoType;
        video.appendChild(sourceDefault);
    } else {
        return; // No valid video sources found
    }

    // Force video to load
    video.load();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoLazyLoading);
} else {
    initVideoLazyLoading();
}

// Re-initialize if new content is added dynamically (e.g., in sliders)
// This will observe any new videos that weren't present during initial load
export function reinitialize() {
    initVideoLazyLoading();
}
